import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useSorting } from "@/hooks/useSorting";
import { GuesteriaPartnerLogo } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerLogo";
import { GuesteriaPartnerConnectionStatusBadge } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerConnectionStatusBadge";
import { GuesteriaPartnerRegionValue } from "@/components/pages/guesteria-partners/components/GuesteriaPartnerRegionValue";
import { formatGuesteriaPartnerRevenueEur } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersFormat";
import type { GuesteriaPartnerRow } from "@/components/pages/guesteria-partners/utils/guesteriaPartnersModel";

export type GuesteriaPartnersTableProps = {
  partners: GuesteriaPartnerRow[];
  onPartnerEnabledChange: (id: string, enabled: boolean) => void;
};

function sortPartners(
  rows: GuesteriaPartnerRow[],
  field: string,
  direction: "asc" | "desc",
): GuesteriaPartnerRow[] {
  const mult = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    switch (field) {
      case "enabled":
        return mult * (Number(a.enabled) - Number(b.enabled));
      case "connectionStatus":
        return mult * a.connectionStatus.localeCompare(b.connectionStatus);
      case "region.city": {
        const ca = a.region?.city ?? "";
        const cb = b.region?.city ?? "";
        return mult * ca.localeCompare(cb);
      }
      case "commissionPercent": {
        const na = a.commissionPercent ?? -Infinity;
        const nb = b.commissionPercent ?? -Infinity;
        return mult * (na - nb);
      }
      case "revenueEur": {
        const na = a.revenueEur ?? -Infinity;
        const nb = b.revenueEur ?? -Infinity;
        return mult * (na - nb);
      }
      default:
        return 0;
    }
  });
}

function PartnerEnabledCell({
  row,
  onToggle,
  statusLabel,
}: {
  row: GuesteriaPartnerRow;
  onToggle: (id: string, enabled: boolean) => void;
  statusLabel: string;
}): React.JSX.Element {
  const handleChange = (checked: boolean) => {
    onToggle(row.id, checked);
  };
  return (
    <div className="flex justify-center">
      <Switch
        className="data-[state=checked]:bg-[var(--royal-glow-2)]"
        checked={row.enabled}
        onClick={(event) => event.stopPropagation()}
        onCheckedChange={handleChange}
        aria-label={statusLabel}
      />
    </div>
  );
}

export const GuesteriaPartnersTable = ({
  partners,
  onPartnerEnabledChange,
}: GuesteriaPartnersTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sortField, sortDirection, handleSort } = useSorting();

  const sortedRows = useMemo(() => {
    if (!sortField || !sortDirection) {
      return partners;
    }
    return sortPartners(partners, sortField, sortDirection);
  }, [partners, sortField, sortDirection]);

  const columns: Column<GuesteriaPartnerRow>[] = useMemo(
    () => [
      {
        key: "enabled",
        header: t("partners.table.status"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) => (
          <PartnerEnabledCell
            row={row}
            onToggle={onPartnerEnabledChange}
            statusLabel={t("partners.table.status")}
          />
        ),
      },
      {
        key: "name",
        header: t("partners.table.partner"),
        sortable: false,
        className: "text-center",
        render: (_v, row) => (
          <Button
            type="button"
            variant="span"
            className="mx-auto flex max-w-[200px] flex-col items-center justify-center gap-0.5 rounded-md font-sans outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/guesteria-partners/${row.id}`);
            }}
            aria-label={t("partners.table.viewPartnerDetails", {
              name: row.name,
            })}
          >
            <GuesteriaPartnerLogo logo={row.logo} variant="table" />
            <span className="max-w-[140px] text-center text-[10px] font-medium leading-none text-foreground">
              {row.name}
            </span>
          </Button>
        ),
      },
      {
        key: "connectionStatus",
        header: t("partners.table.connectionStatus"),
        sortable: true,
        className: "text-center",
        render: (_v, row) => (
          <div className="flex justify-center">
            <GuesteriaPartnerConnectionStatusBadge
              status={row.connectionStatus}
            />
          </div>
        ),
      },
      {
        key: "region.city",
        header: t("partners.table.region"),
        sortable: true,
        className: "text-center",
        render: (_v, row) =>
          row.region ? (
            <GuesteriaPartnerRegionValue
              city={row.region.city}
              flagEmoji={row.region.flagEmoji}
              className="justify-center"
            />
          ) : null,
      },
      {
        key: "commissionPercent",
        header: t("partners.table.commission"),
        sortable: true,
        className: "text-center",
        render: (_v, row) =>
          row.commissionPercent != null ? (
            <span className="text-[10px] font-normal leading-none tabular-nums text-foreground">
              {row.commissionPercent}%
            </span>
          ) : null,
      },
      {
        key: "revenueEur",
        header: t("partners.table.revenuePerMonth"),
        sortable: true,
        className: "text-center",
        render: (_v, row) =>
          row.revenueEur != null ? (
            <span className="text-[10px] font-normal leading-none tabular-nums text-foreground">
              {formatGuesteriaPartnerRevenueEur(row.revenueEur)}
            </span>
          ) : null,
      },
      {
        key: "id",
        header: t("partners.table.actions"),
        sortable: false,
        className: "w-[72px] text-center",
        render: (_v, row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="mx-auto h-8 w-8 text-muted-foreground"
            aria-label={t("partners.table.viewPartnerDetails", {
              name: row.name,
            })}
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/guesteria-partners/${row.id}`);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [t, navigate, onPartnerEnabledChange],
  );

  return (
    <DataTable<GuesteriaPartnerRow>
      data={sortedRows}
      columns={columns}
      getRowKey={(row) => row.id}
      withPagination={false}
      withSorting
      columnHeaderClassName="text-[10px]"
      sorting={{
        field: sortField,
        direction: sortDirection,
        onSort: handleSort,
      }}
      emptyMessage={t("partners.table.empty")}
    />
  );
};
